import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Injectable,
  Injector,
  ComponentRef,
  InjectionToken,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ResponsiveService } from 'src/app/common/services/responsive.service';
import { CalendarComponent } from '../../calendar/calendar.component';

export interface CalendarConfig {
  enableTimeSelection?: boolean;
  disableDate?: (date: Date) => boolean;
  positionStrategy?: PositionStrategy;
  defaultDateTimeEpoch?: number;
}

export const CALENDAR_CONFIG = new InjectionToken<CalendarConfig>(
  'CALENDAR_CONFIG'
);

@Injectable()
export class CalendarService {
  private overlayRef: OverlayRef | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private _overlay: Overlay,
    private _injector: Injector,
    private _responsiveService: ResponsiveService
  ) {}

  open(
    target: Element,
    config?: CalendarConfig
  ): { afterClosed: () => Observable<number> } {
    this.dispose();

    const closeSubject = new Subject<number>();
    const positionStrategy =
      config?.positionStrategy || this.getDefaultPositionStrategy(target);

    this.overlayRef = this._overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
    });

    const injector = this.createInjector(config || {});
    const calendarPortal = new ComponentPortal(
      CalendarComponent,
      null,
      injector
    );

    const calendarRef: ComponentRef<CalendarComponent> =
      this.overlayRef.attach(calendarPortal);

    // Handle calendar selection
    calendarRef.instance.apply
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        closeSubject.next(result);
        closeSubject.complete();
        this.dispose();
      });

    calendarRef.instance.cancel.pipe(takeUntil(this.destroy$)).subscribe(() => {
      closeSubject.next();
      closeSubject.complete();
      this.dispose();
    });

    // Handle backdrop click
    this.overlayRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => {
        closeSubject.next();
        closeSubject.complete();
        this.dispose();
      });

    return {
      afterClosed: () => closeSubject.asObservable(),
    };
  }

  private createInjector(config: CalendarConfig): Injector {
    return Injector.create({
      providers: [{ provide: CALENDAR_CONFIG, useValue: config }],
      parent: this._injector,
    });
  }

  private getDefaultPositionStrategy(target: Element): PositionStrategy {
    const isMobile = this._responsiveService.isMobile();
    if (isMobile) {
      // Open from bottom for mobile devices
      return this._overlay
        .position()
        .global()
        .centerHorizontally()
        .bottom('0px');
    } else {
      return this._overlay
        .position()
        .flexibleConnectedTo(target)
        .withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 8,
          },
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -8,
          },
        ])
        .withFlexibleDimensions(false)
        .withPush(true);
    }
  }

  private dispose(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.destroy$.next();
      this.destroy$.complete();
    }
  }
}
